import React from 'react'
import classNames from 'classnames'
import { withStyles } from '@material-ui/core/styles'
import { Redirect } from 'react-router-dom'
import { Grid, Typography, CircularProgress, Tabs, Tab, Avatar, Menu, MenuItem, Badge, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from '@material-ui/core'
import { AppBar, Container, Panel, Tooltip, Button, IconButton } from '@michelin/acid-components'
import { Menu as MenuIcon, Save as SaveIcon, Search as SearchIcon, SettingsPhone as SettingsPhoneIcon, ExitToApp as ExitToAppIcon, CheckCircle as CheckCircleIcon, Edit as EditIcon } from '@material-ui/icons'
import { Link } from "react-router-dom"
import { Prompt } from 'react-router'
import PropTypes from 'prop-types'
import MaterialHeadline from './MaterialHeadline'
import MaterialInputable from './MaterialInputable'
import MaterialSelectable from './MaterialSelectable'
import Contact from './Contact'
import AccountNumbers from './AccountNumbers'
import Tire from './Tire'
import Wheel from './Wheel'
import Address from './Address'
import AddressDialog from './AddressDialog'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.min.css'
import moment from 'moment'
import FormValidator from './FormValidator'
import clsx from 'clsx'

const styles = theme => ({
	saving: {
		height: '100vh'
	},
	container: {
		paddingTop: '80px',
	},
	flex: {
		flexGrow: 1,
	},
	userInfo: {
		fontSize: '14px',
		paddingRight: theme.spacing.unit * 3, 
		borderRight: '1px solid #fff'
	},
	button: {
		margin: theme.spacing.unit,
	},
	extendedIcon: {
		marginRight: theme.spacing.unit,
	},
	leftIcon: {
		marginRight: theme.spacing.unit,
	},
	rightIcon: {
		marginLeft: theme.spacing.unit,
	},
	iconSmall: {
		fontSize: 20,
	},
	margin: {
		margin: theme.spacing.unit * 2,
	},
	topMargin: {
		marginTop: theme.spacing.unit
	},
	padding: {
		padding: `0 ${theme.spacing.unit * 2}px`,
	}, 
	bigIndicator: {
		height: 5,
	},
	logo: {
		width: '4rem',
		height: '4rem',
		marginRight: '1rem',
	},
	menuButton: {
		marginRight: '1rem',
	},
	panelButton: {
		position: 'relative',
		top: 3
	}
})

class HomePage extends React.Component {
	/**
	 * Default constructor for the object
	 * @param {props} props - the default props for the object
	 * @returns {HomePage} the newly constructed HomePage object
	 * @public
	 */
	constructor(props) {
		super(props)

		this.validator = this.getValidator(props)

		this.references = []

		this.state = {
			axleType: 1,
			axleSteerCount: 0,
			axleDriveCount: 0,
			axleTrailerCount: 0,
			addressDialogOpen: false,
			anchorEl: null,
			validation: this.validator.valid()
		}

		this.handleMenuOpen = this.handleMenuOpen.bind(this)
		this.handleMenuClose = this.handleMenuClose.bind(this)
		this.editAddressClick = this.editAddressClick.bind(this)
		this.addressDialogClose = this.addressDialogClose.bind(this)
		this.addressDialogUpdate = this.addressDialogUpdate.bind(this)
		this.onAxleTypeChange = this.onAxleTypeChange.bind(this)
		this.onSaveClick = this.onSaveClick.bind(this)
		this.isDisabled = this.isDisabled.bind(this)
		this.validate = this.validate.bind(this)
	}

	/**
	 * Gets the current list of validations for the object
	 * @returns {FormValidator} the current validation object
	 * @public
	 */
	getValidator() {
		return new FormValidator([
			{
				prop: {
					name: 'driverAuthorization'
				},
				field: "driverAuthorization",
				method: 'isEmpty',
				validWhen: false,
				message: 'Driver Authorization is required',
				isDisabled: this.isDisabled('authorization')
			},
			{
				prop: {
					name: 'disposition'
				},
				field: "disposition",
				method: 'isEmpty',
				validWhen: false,
				message: 'Disposition of Off Tire is required',
				isDisabled: this.isDisabled('additionalInformation')
			},
			{
				prop: {
					object: 'poPreference',
					name: 'beforeAfter'
				},
				field: "beforeAfter",
				method: 'isEmpty',
				validWhen: false,
				message: 'Before/After is required',
				isDisabled: this.isDisabled('beforeAfter')
			},
		])
	}

	/**
	 * Handle the menu open event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	handleMenuOpen(event) {
		this.setState({ anchorEl: event.currentTarget })
	}

	/**
	 * Handle the menu close event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	handleMenuClose() {
		this.setState({ anchorEl: null })
	}

	/**
	 * Handle the edit address click event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	editAddressClick() {
		this.setState({ addressDialogOpen: true })
	}

	/**
	 * Handle the address dialog close event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	addressDialogClose() {
		this.setState({ addressDialogOpen: false })
	}

	/**
	 * Handle the address dialog update event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	addressDialogUpdate(addressId, address, accountNumber, accountName) {
		this.props.accountEdit(accountNumber, accountName)
		this.props.addressChange(address)
	}

	/**
	 * Handle the axleType change event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	onAxleTypeChange(event, value) {
		this.setState({ axleType: value })
	}

	/**
	 * Is the section disabled?
	 * @param {string} section - the section to check
	 * @returns {boolean} true if the section is disabled
	 * @public
	 */
	isDisabled(section) {
		let { isBillTo, accountType, lbbId, roles, country, homePageDomain } = this.props
		
		if (accountType === 'NAFA') 
			return true

		if (homePageDomain.id === undefined)
			return true

		if (accountType === lbbId) {
			if (roles.includes('michelinops') || roles.includes('michelin_ops'))
				return true
			
			return false
		}

		if (accountType !== lbbId) {
			if (['poRequired', 'ccRequired'].includes(section)) {
				return true
			}
		}

		if ((roles.includes('eagle') || roles.includes('eagle_supervisor')) && country === 'USA')
			return true

		if (isBillTo)
			return true

		return false
	}

	/**
	 * get the lastest (maximum) updatedAt date from the list
	 * @param {array} items - the list of items
	 * @returns {moment} the maximum date found
	 * @public
	 */
	getLastUpdatedAt(items) {
		if (items.length === 0) 
			return ''
			
		const dates = items.map(item => { return item.updatedAt })
		return moment.max(dates)
	}

	/**
	 * get all of the current updatedAt and sources for the object
	 * @returns {updateInfo} an object containing the source and modifiedDates for the sections
	 * @public
	 */
	getUpdateInfo() {
		let updateInfo = {
			otherAccounts: {
				source: 'No profile data',
				modifiedDate: this.getLastUpdatedAt(this.props.otherAccountNumbers)
			},
			tires: {
				source: 'No profile data',
				modifiedDate:  this.getLastUpdatedAt(this.props.tires)
			},
			wheels: {
				source: 'No profile data',
				modifiedDate: this.getLastUpdatedAt(this.props.wheels)
			},
			poPreferences: {
				source: 'No profile data',
				modifiedDate: this.getLastUpdatedAt([this.props.poPreference])
			},
		}

		if (this.props.accountType === this.props.nafaId) {
			if (this.props.otherAccountNumbers.length > 0) {
				updateInfo.otherAccounts.source = 'Bill To'
				if (!this.props.otherAccountNumbers[0].isBillToPreference) {
					updateInfo.otherAccounts.source = 'Ship To'
				}
			}

			if (this.props.tires.length > 0) {
				updateInfo.tires.source = 'Bill To'
				if (!this.props.tires[0].isBillToPreference) {
					updateInfo.tires.source = 'Ship To'
				}
			}

			if (this.props.wheels.length > 0) {
				updateInfo.wheels.source = 'Bill To'
				if (!this.props.wheels[0].isBillToPreference) {
					updateInfo.wheels.source = 'Ship To'
				}
			}

			if (this.props.poPreference.isBillToPreference) {
				updateInfo.poPreferences.source = 'Bill To'
			} else {
				updateInfo.poPreferences.source = 'Ship To'
			}
		} else {
			updateInfo.otherAccounts.source = 'Local Account'
			updateInfo.tires.source = 'Local Account'
			updateInfo.wheels.source = 'Local Account'
			updateInfo.poPreferences.source = 'Local Account'
		}

		return updateInfo
	}

	/**
	 * Handle the onSaveClick event
	 * @param {event} event - the current event
	 * @returns {void} void
	 * @public
	 */
	async onSaveClick(event) {
		const isValid = await this.validate()

		if (isValid) {
			if (this.props.onSave) {
				this.props.onSave()
			}
		} else {
			toast.error('Please correct the displayed errors and try again')
		}
	}

	/**
	 * Validates the current form data
	 * @returns {boolean} true if the current data is valid
	 * @public
	 */
	async validate() {
		const validation = await this.validator.validate(this.state, this.props)
		this.setState({ validation })

		let isValid = validation.isValid

		await Promise.all(this.references.map(async component => {
			return component.current.isValid()
		})).then(values => {
			for(const value of values) {
				if (!value)
					isValid = false
			}
		})

		return isValid
	}

	/**
	 * clear the references array
	 * @returns {void} void
	 * @public
	 */
	clearReferences() {
		/**
		 * Clear the specified array
		 * @param {array} array - the array to empty
		 * @returns {void} void
		 * @public
		 */
		function clearArray(array) {
			while (array.length) {
				array.pop()
			}
		}

		clearArray(this.references)
	}

	/**
	 * create a new React.ref object 
	 * @param {string} - the section to hold the reference
	 * @returns {ref} - the reference
	 * @public
	 */
	getReference(section) {
		const reference = React.createRef()

		this.references.push(reference)

		return reference
	}

	render() {
		const { classes, logout, handleAccountSetupChangeValue, accountTypeOptions, countryTypeOptions, country, nafaId, lbbId, isBillTo } = this.props
		const { axleType, addressDialogOpen, anchorEl, validation } = this.state

		const props = this.props

		if (this.props.saveSnack) {
			toast.success("Save Complete")
		}

		if (this.props.errorSnack) {
			toast.error(this.props.errorMessage)
		}

		const axleSteerCount = this.props.tires.filter(item => { return item.axleType === 1 && !item.isDelete }).length
		const axleDriveCount = this.props.tires.filter(item => { return item.axleType === 2 && !item.isDelete }).length
		const axleTrailerCount = this.props.tires.filter(item => { return item.axleType === 3 && !item.isDelete }).length
		const axleOtherCount = this.props.tires.filter(item => { return (![1, 2, 3].includes(item.axleType)) && !item.isDelete }).length

		const updateInfo = this.getUpdateInfo()

		this.clearReferences()

		if (props.homePageDomain.id === undefined) {
			return (
				<Redirect to="/" />
			)
		}

		return (
			props.isSaving === true ?
				<Grid container justify="center" className={classes.saving} alignItems="center" alignContent="center">
					<CircularProgress size={50} className={classes.button} />
					<Typography variant="display3" >Saving...</Typography>
				</Grid>
				:
				<form className={classes.container}>
					<ToastContainer position="top-center" />

					<Prompt
						when={props.unsafeCancelOrSearch}
						message="Are you sure you want to leave? There are unsaved changed"
					/>

					{props.accountType === lbbId && props.homePageDomain.id && props.address.id &&
					<AddressDialog
						open={addressDialogOpen}
						countryTypeOptions={countryTypeOptions}
						accountId={props.homePageDomain.id}
						accountNumber={props.storeAccountNumber}
						accountName={props.fleetName}
						addressId={props.address.id}
						address={props.address}
						onClose={this.addressDialogClose}
						onUpdate={this.addressDialogUpdate}
					/>
					}

					<AppBar fixed>
						<Avatar src="/bib.jpg" className={classes.logo} />

						<IconButton className={classes.menuButton} aria-haspopup='true' onClick={this.handleMenuOpen}>
							<MenuIcon />
						</IconButton>

						<Menu
							id="menu-appbar"
							anchorEl={anchorEl}
							keepMounted
							open={Boolean(anchorEl)}
							onClose={this.handleMenuClose}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'left',
							}}
						>
							<Link to='/inbound' style={{ textDecoration: 'none', outline: 'none', color: 'inherit' }}>
								<MenuItem>
									<SettingsPhoneIcon className={clsx(classes.leftIcon, classes.iconSmall)} />Inbound Program Maintenance
								</MenuItem>
							</Link>

							<MenuItem onClick={() => logout()}>
								<ExitToAppIcon className={clsx(classes.leftIcon, classes.iconSmall)} />Logout
							</MenuItem>
						</Menu>

						<div style={{ display: 'inline-block', flexGrow: 1 }}>
							<span style={{ fontWeight: 700, marginRight: '.5rem' }}>ERS Profile</span>
							<span> Account Maintenance</span>
						</div>

						<Typography align="right" color="inherit" className={[classes.flex, classes.userInfo].join(' ')}>
							Welcome, {this.props.username}
						</Typography>

						{props.homePageDomain.id !== undefined &&
							<Button color="primary" onClick={this.onSaveClick} disabled={this.isDisabled('saveButton') || !props.unsafeCancelOrSearch} style={{ marginRight: 8 }}>
								<SaveIcon className={classNames(classes.leftIcon, classes.iconSmall)} />
								Save
							</Button>
						}

						<Link to='/' style={{ textDecoration: 'none', outline: 'none', color: 'inherit' }}>
							<Button color="primary">
								<SearchIcon className={classNames(classes.leftIcon, classes.iconSmall)} />
								Return
							</Button>
						</Link>
					</AppBar>

					<Container>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Panel 
									title="Account Setup" 
									spacing={1}
								>
									<Grid container>
										<Grid item xs={4} sm={4} md={2} lg={2}>
											<MaterialSelectable
												required
												disabled
												fullWidth
												value={props.accountType}
												name="accountType"
												label="Account Type"
												onChange={handleAccountSetupChangeValue}
												options={accountTypeOptions} />
										</Grid>
										<Grid item xs={8} sm={8} md={4}>
											<MaterialSelectable
												required
												disabled
												fullWidth
												value={props.country}
												name="country"
												label="Country"
												onChange={handleAccountSetupChangeValue}
												options={countryTypeOptions} />
										</Grid>
									</Grid>

									{props.accountType === nafaId &&
										<Grid container spacing={1}>
											<Grid item xs={12} sm={4} md={4} lg={3}>
												<MaterialInputable
													required
													disabled
													name="fleetName"
													label="Fleet Name"
													value={props.fleetName}
													onChange={props.handleDomainChangeValue} />
											</Grid>
											<Grid item xs={6} sm={2} md={2} lg={2}>
												<MaterialInputable
													required
													disabled
													name="billTo"
													label="Bill To #"
													value={props.billTo}
													onChange={handleAccountSetupChangeValue} />
											</Grid>
											{props.shipTo !== undefined &&
												<Grid item xs={6} sm={2} md={2} lg={2}>
													<MaterialInputable
														required
														disabled
														name="shipTo"
														label="Ship To #"
														value={props.shipTo}
														onChange={handleAccountSetupChangeValue} />
												</Grid>
											}
											<Grid item xs={12} sm={12} md={12} lg={9} xl={6}>
												<Address
													disabled
													extendedAddress
													values={props.address}
													country={country}
													onChange={handleAccountSetupChangeValue}
													onSelectChange={handleAccountSetupChangeValue}
													layout='horizontal' />
											</Grid>
										</Grid>
									}

									{props.accountType === lbbId &&
										<Grid container spacing={1}>
											<Grid item xs={12} sm={5} md={4} lg={2} xl={2}>
												<MaterialInputable
													required
													disabled
													name="storeAccountNumber"
													label="Fleet Store Account Number"
													value={props.storeAccountNumber}
													autoUpdate
													onChange={handleAccountSetupChangeValue} />
											</Grid>
											<Grid item xs={12} sm={7} md={5} lg={3} xl={3}>
												<MaterialInputable
													required
													disabled
													name="fleetName"
													label="Customer Name"
													value={props.fleetName}
													autoUpdate
													onChange={handleAccountSetupChangeValue} />
											</Grid>
											<Grid item xs={12} sm={7} md={5} lg={3} xl={3}>
												<MaterialInputable
													required
													disabled
													name="dealerName"
													label="Dealer Name"
													value={props.dealerName}
													onChange={props.handleDomainChangeValue} />
											</Grid>
											<Grid item xs={12} sm={5} md={4} lg={2} xl={2}>
												<MaterialInputable
													required
													disabled
													name="dealerBt"
													label="Dealer BT"
													value={props.dealerBt}
													onChange={props.handleDomainChangeValue} />
											</Grid>
											<Grid item xs={12} sm={12} md={11} lg={10} xl={7}>
												<Address
													disabled
													extendedAddress
													autoUpdate
													values={props.address}
													onChange={handleAccountSetupChangeValue}
													onSelectChange={handleAccountSetupChangeValue}
													layout='horizontal' />
											</Grid>
											<Grid item xs={12} sm={12} md={1} lg={2} xl={1}>
												<Tooltip title="Edit Account">
													<IconButton className={classes.button} color="primary" onClick={this.editAddressClick}>
														<EditIcon />
													</IconButton>
												</Tooltip>
											</Grid>
										</Grid>
									}
								</Panel>
							</Grid>

							<Grid item xs={12}>
								<Panel 
									title="Contacts"
									control={
										<Button 
											disabled={this.isDisabled('contact')} 
											color="primary" 
											onClick={() => props.addContact()}
											style={{ position: 'relative', top: 3 }}
										>
											Add Another Contact
										</Button>
										}
									spacing={1}
								>
									{
										props.contact.map((item, i) => (
											<Contact
												disabled={this.isDisabled('contact')}
												contactTypes={props.contactTypes}
												domain={item}
												key={i}
												index={i}
												ref={this.getReference('contacts')}
												onChange={props.handleContactChangeValue}
												onChangeSelect={props.handleContactChangeValueSelect}
												onRemove={() => props.removeContact(i)} />
										))
									}
									{
										props.accountType === nafaId && isBillTo && props.contact.length === 0 &&
										<Typography className={classes.topMargin} color={'secondary'} variant={'subheading'}>No Bill To level profile data available from Michelin B2B</Typography>
									}
								</Panel>
							</Grid>

							{props.accountType !== lbbId &&
							<Grid item xs={12} md={7}>
								<Panel 
									title={
										<MaterialHeadline 
											title='Other Account Numbers' 
											source={updateInfo.otherAccounts.source} 
											modifiedDate={updateInfo.otherAccounts.modifiedDate} 
										/>}
									control={
										<Button 
											disabled={this.isDisabled('otherAccounts')} 
											color="primary" 
											onClick={props.addOtherAccountNumbers}
											style={{ position: 'relative', top: 3 }}
										>
											Add Another Account
										</Button>}
									spacing={1}
								>
									{
										props.otherAccountNumbers.map((item, i) => (
											<AccountNumbers
												domain={item}
												key={i}
												index={i}
												ref={this.getReference('otherAccounts')}
												disabled={this.isDisabled('otherAccounts')}
												onChange={props.handleAccountNumbersChange}
												onChangeSelect={props.handleAccountNumbersChangeSelect}
												onRemove={props.removeOtherAccountNumbers}
												accountTypes={props.otherAccountTypes} />
										))
									}
									{
										props.accountType === nafaId && isBillTo && props.otherAccountNumbers.length === 0 &&
										<Typography className={classes.topMargin} color={'secondary'} variant={'subheading'}>No Bill To level profile data available from Michelin B2B</Typography>
									}
								</Panel>
							</Grid>
							}

							<Grid item xs={12} md={5}>
								<Panel 
									title={
										<MaterialHeadline 
											title='Billing Preference' 
											source={updateInfo.poPreferences.source} 
											modifiedDate={updateInfo.poPreferences.modifiedDate}
										/>} 
									spacing={1}
								>
									<Grid container spacing={1}>
										<Grid item xs={5}>
											<FormControl component='fieldset' className={classes.formControl} disabled={this.isDisabled('poRequired')}>
												<FormLabel component='legend'>PO Required</FormLabel>
												<RadioGroup
													aria-label='PO Required'
													name="poRequired"
													value={props.poPreference.poRequired ? 'true' : 'false'}
													row={true}
													style={{ display: 'flex' }}
													onChange={props.poRequiredChange}>
													<FormControlLabel
														value={'true'}
														control={<Radio color="primary" />}
														label="Yes" />
													<FormControlLabel
														value={'false'}
														control={<Radio color="primary" />}
														label="No" />
												</RadioGroup>
											</FormControl>
										</Grid>
										<Grid item xs={7}>
											<MaterialSelectable
												required
												name="beforeAfter"
												label="Before/After"
												disabled={this.isDisabled('beforeAfter')}
												value={props.poPreference.beforeAfter}
												error={validation.beforeAfter.isInvalid}
												errorMessage={validation.beforeAfter.message}
												onChange={props.poRequiredChange}
												options={props.beforeAfterOptions} />
										</Grid>
										<Grid item xs={12}>
											<FormControl component='fieldset' className={classes.formControl} disabled={this.isDisabled('ccRequired')}>
												<FormLabel component='legend'>CC Required</FormLabel>
												<RadioGroup
													aria-label='CC Required'
													name="ccRequired"
													value={props.poPreference.ccRequired || 'M'}
													row={true}
													style={{ display: 'flex' }}
													onChange={props.ccRequiredChange}>
													<FormControlLabel
														value={'2'}
														control={<Radio color="primary" />}
														label="Yes" />
													<FormControlLabel
														value={'3'}
														control={<Radio color="primary" />}
														label="No" />
													<FormControlLabel
														value={'1'}
														control={<Radio color="primary" />}
														label="Optional" />
												</RadioGroup>
											</FormControl>
										</Grid>
									</Grid>
								</Panel>
							</Grid>

							<Grid item xs={12}>
								<Panel
									title={
										<MaterialHeadline
											title='Service Preferences'
											source={updateInfo.tires.source}
											modifiedDate={updateInfo.tires.modifiedDate}
										/>}
									control={
										<Button
											disabled={this.isDisabled('tires')}
											color="primary"
											onClick={props.addTire}
											style={{ position: 'relative', top: 3 }}
										>
											Add Another Tire Preference
										</Button>}
									spacing={1}
								>
									<AppBar position="static">
										<Tabs classes={{ indicator: classes.bigIndicator }} value={axleType} onChange={this.onAxleTypeChange}>
											<Tab value={1} label={<Badge className={classes.padding} badgeContent={axleSteerCount} color="secondary">Steer</Badge>} />
											<Tab value={2} label={<Badge className={classes.padding} badgeContent={axleDriveCount} color="secondary">Drive</Badge>} />
											<Tab value={3} label={<Badge className={classes.padding} badgeContent={axleTrailerCount} color="secondary">Trailer</Badge>} />
											<Tab value={4} label={<Badge className={classes.padding} badgeContent={axleOtherCount} color="secondary">Other</Badge>} />
										</Tabs>
									</AppBar>

									{
										props.tires.map((datTire, i) => {
											datTire.index = i
											datTire.axleTypeGroup = datTire.axleType < 4 ? datTire.axleType : 4
											return datTire
										}).sort(function (a, b) {
											if (a.isCreate > b.isCreate) return 1
											if (a.isCreate < b.isCreate) return -1

											if (a.axleType < b.axleType) return 1
											if (a.axleType > b.axleType) return -1

											if (a.application < b.application) return 1
											if (a.application > b.application) return -1

											if (a.sizeDesc < b.sizeDesc) return -1
											if (a.sizeDesc > b.sizeDesc) return 1

											const ap = parseInt(a.priority, 10)
											const bp = parseInt(b.priority, 10)

											if (ap < bp) return -1
											if (ap > bp) return 1

											return 0
										}).map((datTire, i) => {
											datTire.displayIndex = i
											return datTire
										}).map(item => (
											(item.axleTypeGroup !== axleType && !item.isCreate) ? null :
												<Tire domain={item}
													key={item.index}
													index={item.index}
													ref={this.getReference('tires')}
													disabled={this.isDisabled('servicePreference')}
													displayIndex={item.displayIndex}
													isNafa={props.accountType === nafaId}
													onChange={props.tireChange}
													onChangeSelect={props.tireChangeSelect}
													onAdd={props.addTire}
													onRemove={props.removeTire}
													onDuplicate={props.duplicateTire}
													axleTypeOptions={props.axleTypeOptions}
													recordCount={props.tires.filter(t => !t.isDelete).length}
													tireApplicationTypeOptions={props.tireApplicationTypeOptions}
													requiredActivityTypeOptions={props.requiredActivityTypeOptions}
													tireTypeOptions={props.tireTypeOptions}
													tireBrandTypeOptions={props.tireBrandTypeOptions}
													tireSizeTypeOptions={props.tireSizeTypeOptions}
													tireTreadTypeOptions={props.tireTreadTypeOptions}
													loadRangeTypeOptions={props.loadRangeTypeOptions} />
										))
									}
									{
										props.accountType === nafaId && isBillTo && props.tires.length === 0 &&
										<Typography className={classes.topMargin} color={'secondary'} variant={'subheading'}>No Bill To level profile data available from Michelin B2B</Typography>
									}
								</Panel>
							</Grid>

							<Grid item xs={12}>
								<Panel
									spacing={1}
									title={
										<MaterialHeadline 
											title="Wheel Types" 
											source={updateInfo.wheels.source} 
											modifiedDate={updateInfo.wheels.modifiedDate} 
											/>}
									control={
										<Button 
											disabled={this.isDisabled('wheelTypes')} 
											color="primary" 
											onClick={props.addWheel}
											style={{ position: 'relative', top: 3 }}
										>
											Add Another Wheel Type
										</Button>}
								>
									<Grid container spacing={1}>
										<Grid item xs={12}>
											{
												props.wheels.map((item, i) => (
													<Wheel
														domain={item}
														key={i}
														index={i}
														ref={this.getReference('wheels')}
														recordCount={props.wheels.filter(w => !w.isDelete).length}
														disabled={this.isDisabled('wheelTypes')}
														onChange={props.wheelChange}
														onChangeSelect={props.wheelChangeSelect}
														onAdd={props.addWheel}
														onRemove={props.removeWheel}
														wheelDiameterOptions={props.wheelDiameterOptions}
														wheelOffsetOptions={props.wheelOffsetOptions}
														wheelMaterialTypeOptions={props.wheelMaterialTypeOptions}
														wheelTypeOptions={props.wheelTypeOptions} />
												))
											}
											{
												props.accountType === nafaId && isBillTo && props.wheels.length === 0 &&
												<Typography className={classes.topMargin} color={'secondary'} variant={'subheading'}>No Bill To level profile data available from Michelin B2B</Typography>
											}
										</Grid>
									</Grid>
								</Panel>
							</Grid>

							<Grid item xs={12} sm={6} md={3}>
								<Panel title="Authorization">
									<MaterialSelectable
										required
										name="driverAuthorization"
										label="Driver is Authorized"
										value={props.driverAuthorization}
										disabled={this.isDisabled('authorization')}
										error={validation.driverAuthorization.isInvalid}
										errorMessage={validation.driverAuthorization.message}
										onChange={props.handleAccountSetupChangeValue}
										options={props.driverAuthorizationTypeOptions} />
								</Panel>
							</Grid>

							<Grid item xs={12} sm={6} md={9}>
								<Panel title="Additional Information">
									<Grid container spacing={1} className={classes.gridContainerNoPad}>
										<Grid item xs={12} md={4}>
											<MaterialSelectable
												required
												name="disposition"
												label="Disposition of Off Tire"
												value={props.disposition || ''}
												disabled={this.isDisabled('additionalInformation')}
												error={validation.disposition.isInvalid}
												errorMessage={validation.disposition.message}
												onChange={props.handleAccountSetupChangeValue}
												options={props.dispositionOfOffTireTypeOptions} />
										</Grid>
										<Grid item xs={12} md={8}>
											<MaterialInputable
												name="ersInstructions"
												label="ERS Instructions"
												disabled={this.isDisabled('additionalInformation')}
												multiline
												onChange={props.handleAccountSetupChangeValue}
												value={props.ersInstructions || ''} />
										</Grid>
									</Grid>
								</Panel>
							</Grid>
						
							<Grid item xs={12}>
								{props.homePageDomain.id !== undefined &&
									<Button color="primary" aria-label="Save" className={classes.button} onClick={this.onSaveClick} disabled={this.isDisabled('saveButton') || !props.unsafeCancelOrSearch}>
										<SaveIcon className={classes.extendedIcon} />
										Save
									</Button>
								}

								{process.env.NODE_ENV !== 'production' &&
									<Button color="primary" aria-label="Validate" className={classes.button} onClick={this.validate} style={{ marginLeft: 8 }}>
										<CheckCircleIcon className={classes.extendedIcon} />
										Validate
									</Button>
								}
							</Grid>
						</Grid>
					</Container>
				</form>
		)
	}
}

HomePage.propTypes = {
	roles: PropTypes.array,
	username: PropTypes.string,
	environment: PropTypes.string,
	classes: PropTypes.object,
	logout: PropTypes.func,
	onSave: PropTypes.func,
	onCancel: PropTypes.func,
	nafaId: PropTypes.string,
	lbbId: PropTypes.string,
	isSaving: PropTypes.bool,
	saveSnack: PropTypes.bool,
	errorSnack: PropTypes.bool,
	errorMessage: PropTypes.string,
	snackClose: PropTypes.func,
	unsafeCancelOrSearch: PropTypes.bool,
	fleetName: PropTypes.string,
	fleetLocationIdentifier: PropTypes.string,
	billTo: PropTypes.string,
	shipTo: PropTypes.string,
	isBillTo: PropTypes.bool,
	isShipTo: PropTypes.bool,
	accountType: PropTypes.string,
	address: PropTypes.object,
	country: PropTypes.string,
	addressChange: PropTypes.func,
	accountEdit: PropTypes.func,
	profileName: PropTypes.string,
	dealerName: PropTypes.string,
	dealerBt: PropTypes.string,
	storeAccountNumber: PropTypes.string,
	handleAccountSetupChangeValue: PropTypes.func,
	accountTypeOptions: PropTypes.array,
	countryTypeOptions: PropTypes.array,
	dealerNameOptions: PropTypes.array,
	dealerBtOptions: PropTypes.array,
	dealerStOptions: PropTypes.array,
	lbbSearch: PropTypes.func,
	wheels: PropTypes.array,
	wheelChange: PropTypes.func,
	addWheel: PropTypes.func,
	removeWheel: PropTypes.func,
	wheelDiameterOptions: PropTypes.array,
	wheelOffsetOptions: PropTypes.array,
	wheelMaterialTypeOptions: PropTypes.array,
	wheelTypeOptions: PropTypes.array,
	tires: PropTypes.array,
	tireChange: PropTypes.func,
	addTire: PropTypes.func,
	removeTire: PropTypes.func,
	duplicateTire: PropTypes.func,
	axleTypeOptions: PropTypes.array,
	requiredActivityTypeOptions: PropTypes.array,
	tireApplicationTypeOptions: PropTypes.array,
	tireTypeOptions: PropTypes.array,
	tireBrandTypeOptions: PropTypes.array,
	tireSizeTypeOptions: PropTypes.array,
	tireTreadTypeOptions: PropTypes.array,
	loadRangeTypeOptions: PropTypes.array,
	wheelChangeSelect: PropTypes.func,
	tireChangeSelect: PropTypes.func,
	otherAccountNumbers: PropTypes.array,
	handleAccountNumbersChange: PropTypes.func,
	handleAccountNumbersChangeSelect: PropTypes.func,
	removeOtherAccountNumbers: PropTypes.func,
	otherAccountTypes: PropTypes.array,
	addOtherAccountNumbers: PropTypes.func,
	contact: PropTypes.array,
	contactTypes: PropTypes.array,
	handleContactChangeValue: PropTypes.func,
	handleContactChangeValueSelect: PropTypes.func,
	addContact: PropTypes.func,
	poPreference: PropTypes.object,
	beforeAfterOptions: PropTypes.array,
	poRequiredChange: PropTypes.func,
	ccRequiredChange: PropTypes.func,
	driverAuthorization: PropTypes.number,
	driverAuthorizationTypeOptions: PropTypes.array,
	disposition: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	dispositionOfOffTireTypeOptions: PropTypes.array,
	homePageDomain: PropTypes.object,
	handleDomainChangeValue: PropTypes.func,
}

export default withStyles(styles)(HomePage)



// WEBPACK FOOTER //
// ./src/components/HomePage.js